import { useCallback, useEffect, useRef, useState } from "react";
import { notificationsAPI } from "../services/notificationsAPI";

const errorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

// Preserve the existing hook name and returned fields for all notification consumers.
export function useNotifications({ limit = 50, pollMs = 15000 } = {}) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(false);
  const generation = useRef(0);
  const reading = useRef(false);
  const writing = useRef(false);

  const reload = useCallback(async () => {
    if (!mounted.current || writing.current || reading.current) return;
    const request = ++generation.current;
    reading.current = true;
    setLoading(true);
    try {
      const [listResponse, countResponse] = await Promise.all([
        notificationsAPI.list(limit),
        notificationsAPI.unreadCount(),
      ]);
      if (!mounted.current || request !== generation.current) return;
      setItems(Array.isArray(listResponse?.data) ? listResponse.data : []);
      setUnread(Number(countResponse?.data?.unread) || 0);
      setError(null);
    } catch (requestError) {
      if (mounted.current && request === generation.current) {
        setError(errorMessage(requestError, "Could not refresh notifications. Please retry."));
      }
    } finally {
      if (mounted.current && request === generation.current) {
        reading.current = false;
        setLoading(false);
      }
    }
  }, [limit]);

  const saveRead = useCallback(async (id, all = false) => {
    if (!mounted.current || writing.current) return false;
    writing.current = true;
    // Invalidate any poll started before the write; it must not restore stale unread data.
    ++generation.current;
    reading.current = false;
    setLoading(false);
    setMarking(true);
    setError(null);
    let saved = false;
    try {
      const response = await (all
        ? notificationsAPI.markAllRead()
        : notificationsAPI.markRead(id));
      saved = true;
      if (mounted.current) {
        setItems((current) => current.map((item) =>
          all || String(item.id) === String(id) ? { ...item, is_read: true } : item,
        ));
        if (all) setUnread(Number(response?.data?.unread) || 0);
      }
    } catch (requestError) {
      if (mounted.current) {
        setError(errorMessage(requestError, "Could not mark notifications as read. Please retry."));
      }
    } finally {
      writing.current = false;
      if (mounted.current) setMarking(false);
    }
    // Refill the first page with older unread items and obtain the authoritative count.
    if (saved) await reload();
    return saved;
  }, [reload]);

  const markRead = useCallback((id) => saveRead(id), [saveRead]);
  const markAllRead = useCallback(() => saveRead(null, true), [saveRead]);

  useEffect(() => {
    mounted.current = true;
    reload();
    const timer = pollMs > 0 ? window.setInterval(reload, pollMs) : null;
    return () => {
      mounted.current = false;
      ++generation.current;
      reading.current = false;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [pollMs, reload]);

  return { items, unread, loading, marking, error, reload, markRead, markAllRead };
}
